import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Checkbox, ClickAwayListener, FormControlLabel } from '@mui/material';

import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow-icon.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search-icon.svg';
import { ReactComponent as ClearIcon } from '../../assets/icons/clear-icon.svg';

import * as theme from '../../theme';

const CustomDropdown = (props) => {
    const { placeholder, label, data, style, value, name, field, onChange, disabled, isSearch, size, isCheckbox, checkboxLabel, onRemove, multiSelect, handleCheckbox, showAll, inputName } = props;
    const [state, setState] = useState({
        searchItem: null,
        filteredOptions: [],
    });
    const [optionOpen, setOptionOpen] = useState(false);
    const [fieldName, setFieldName] = useState(name);
    const [isSelected, setIsSelected] = useState(false);

    const inputClicked = 'inputClicked';
    const created = 'created';

    const handleItemClick = (item) => {
        onChange(item);
        setIsSelected(true);
        setOptionOpen(true);
    };

    const handleInputChange = (event) => {
        const {
            target: { name, value },
        } = event;

        setState({
            ...state,
            [name]: value,
            filteredOptions: data.filter((d) => d[fieldName].toLowerCase().includes(value.toLowerCase())),
        });
    };

    const clearSearchInput = () => {
        setState({
            ...state,
            searchItem: '',
            filteredOptions: data,
        });
    };

    const handleShowAll = () => {
        setOptionOpen(true);
        handleCheckbox();
    };

    useEffect(() => {
        if (data) {
            setState({
                ...state,
                filteredOptions: data,
            });
        }
    }, [data]);

    const handleOpen = (type) => {
        if (isSearch) {
            if (type === inputClicked) {
                setOptionOpen(true);
            } else if (type === created) setOptionOpen(false);
            setOptionOpen((state) => !state);
        } else setOptionOpen((state) => !state);
    };

    return (
        <ClickAwayListener
            onClickAway={() => {
                setOptionOpen(false);
                clearSearchInput();
            }}
        >
            <div style={{ display: 'inline-block' }}>
                {label && <Label {...props}>{label}</Label>}
                <Container
                    disabled={disabled}
                    optionOpen={optionOpen}
                    style={style}
                    size={size}
                    multiSelect={multiSelect && value.length > 0}
                    onClick={() => {
                        if (disabled) return;
                        handleOpen('');
                    }}
                >
                    <div>
                        {multiSelect && value.length > 0
                            ? value.map((o, i) => {
                                  return (
                                      <SelectedItem key={i}>
                                          {o[name]}
                                          <ClearIcon onClick={() => onRemove(o)} />
                                      </SelectedItem>
                                  );
                              })
                            : !multiSelect && value && name && data.findIndex((o) => o[field] === value[field]) >= 0
                            ? data[data.findIndex((o) => o[field] === value[field])][name]
                            : !name && value
                            ? value
                            : placeholder}
                    </div>
                    <ArrowIcon id="arrow" optionOpen={optionOpen} />
                    {optionOpen && (
                        <OptionContainer>
                            {isSearch && (
                                <SearchContainer>
                                    <SearchIcon />
                                    <SearchOption
                                        placeholder="Search"
                                        name="searchItem"
                                        id="searchItem"
                                        onClick={() => {
                                            handleOpen(inputClicked);
                                        }}
                                        onChange={handleInputChange}
                                    />
                                </SearchContainer>
                            )}

                            {isCheckbox && (
                                <StyledFormControlLabel control={<StyledCheckbox color={theme.colors.blue.main} size="small" checked={showAll} onChange={handleShowAll} />} label={checkboxLabel} />
                            )}

                            {state.filteredOptions.map((o, i) => {
                                return (
                                    <Option
                                        key={i}
                                        {...props}
                                        onClick={() => {
                                            handleItemClick(o);
                                            clearSearchInput();
                                        }}
                                        isLast={i === state.filteredOptions.length - 1}
                                    >
                                        <div>{name ? o[name] : o}</div>
                                    </Option>
                                );
                            })}
                        </OptionContainer>
                    )}
                </Container>
            </div>
        </ClickAwayListener>
    );
};

export default CustomDropdown;

const Label = styled.h5`
    font-size: 12px;
    font-weight: 500;
    color: ${theme.colors.blue.main};
    text-transform: capitalize;
`;

const Container = styled.div`
    position: relative;
    border: 1px solid ${(props) => (props.optionOpen ? theme.colors.blue.main : theme.colors.border)};
    color: ${theme.colors.blue.main};
    background: ${theme.colors.grey.light};
    min-width: ${(props) => (props.size === 'sm' ? '170px' : '435px')};
    border-radius: 6px;
    padding: 5px 10px;
    max-height: 32px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 100;

    div:first-of-type {
        display: flex;
        align-items: center;
    }

    &:hover {
        cursor: ${(props) => (props.disabled ? `not-allowed` : `pointer`)};
        border: 1px solid ${(props) => (props.disabled ? `#e8e8e8` : theme.colors.blue.main)};
        transition: all 0.2s;
    }

    ${(props) => props.disabled && { border: '1px solid #e8e8e8', color: '#e8e8e8', img: { opacity: 0.3 } }}
    #arrow {
        transform: ${(props) => (props.optionOpen ? 'rotate(180deg)' : null)};
        transition: all 0.2s;

        path {
            fill: ${(props) => (props.disabled ? `#e8e8e8` : theme.colors.blue.main)};
        }
    }
`;

const OptionContainer = styled.div`
    position: absolute;
    top: 110%;
    left: 0;
    background-color: #fff;
    filter: drop-shadow(8px 16px 24px rgba(69, 79, 89, 0.04));
    z-index: 90;
    color: ${theme.colors.blue.main};
    border-radius: 0px 0px 6px 6px;
    max-height: 230px;
    overflow-y: scroll;
    width: 100%;
`;

const Option = styled.div`
    border-radius: ${(props) => (props.isLast ? '0px 0px 6px 6px' : null)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 2px 5px;
    z-index: 50;

    img {
        opacity: 0;
        z-index: 95;
        padding-right: 12px;
    }

    div {
        padding: 10px 12px;
    }

    &:hover {
        background: rgba(0, 124, 222, 0.1);
        border-radius: 5px;

        img {
            opacity: 1;
        }
    }
`;

const SearchContainer = styled.div`
    display: flex;
    align-items: center;
    border: 1px solid ${theme.colors.blue.main};
    background-color: rgba(0, 124, 222, 0.1);
    margin: 5px;
    border-radius: 3px;
    padding: 2px 12px;
`;

const SearchOption = styled.input`
    width: 97%;
    margin: 5px;
    color: ${theme.colors.blue.main};
    background-color: transparent;
    border: none;

    &:focus {
        outline: none;
    }

    &::placeholder {
        color: rgba(0, 124, 222, 0.6);
    }
`;

const SelectedItem = styled.span`
    background-color: ${theme.colors.blue.main};
    color: #fff;
    font-size: 12px;
    border-radius: 15px;
    padding: 3px 10px;
    margin-right: 3px;
    display: flex;
    align-items: center;

    svg {
        width: 15px;
        height: auto;
        margin-left: 2px;

        path {
            fill: #fff;
        }

        &:hover {
            opacity: 0.7;
            transition: all 0.3s;
            cursor: pointer;
        }
    }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
    && {
        margin: 5px;
        cursor: default;
        text-transform: uppercase;
        color: ${theme.colors.black};

        .MuiTypography-body1 {
            font-size: 12px;
        }
    }
`;

const StyledCheckbox = styled(Checkbox)`
    && {
        &.MuiCheckbox-root {
            color: ${theme.colors.blue.main};
            padding: 5px;
        }
    }
`;
